import { useEffect, useRef, useState } from 'react';
import { formatDateTime } from '../api';
import { DataTable, type RecordData } from './Workflow';

type Props = { clientId: number; token?: string };
/** Where the call came in, in the owner's words: never the provider's code ("CTM"). */
const SOURCE: Record<string, string> = { LSA: 'Local Services Ads', CTM: 'Tracking number' };
export default function CallRecoveryAlerts(props: Props) {
  return <ScopedRecoveryAlerts key={`${props.clientId}:${props.token || ''}`} {...props} />;
}
function ScopedRecoveryAlerts({ clientId, token }: Props) {
  const [alerts, setAlerts] = useState<RecordData[]>([]), [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  const active = useRef(true);
  async function request(path: string, method = 'GET') {
    const response = await fetch(`/api/notifications/${clientId}/recovery-alerts${path}`, {
      method, credentials: token ? 'omit' : 'include',
      headers: { 'X-NL-AOS-Request': '1', ...(token ? { 'X-NL-AOS-Portal': token } : {}) },
    });
    if (!response.ok) {
      if (response.status === 401 && !token) window.dispatchEvent(new Event('nl-aos-session-expired'));
      throw Error(method === 'GET' ? 'Recovery alerts could not be loaded.' : 'The alert could not be acknowledged.');
    }
    return response.json();
  }
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  useEffect(() => {
    let current = true;
    request('').then(data => { if (current) { setAlerts(data.alerts || []); setError(''); } })
      .catch(e => { if (current) setError(e.message); });
    return () => { current = false; };
  }, [clientId, token, retry]);
  async function acknowledge(id: string) {
    if (busy) return;
    setBusy(id); setError('');
    try { await request(`/${id}`, 'PUT'); if (active.current) setAlerts(rows => rows.filter(row => String(row.id) !== id)); }
    catch (e) { if (active.current) setError((e as Error).message); }
    finally { if (active.current) setBusy(null); }
  }
  if (!alerts.length && !error) return null;
  // Named like the Settings switch that turns it on ("Show calls worth calling back").
  return <section className="panel workflow-panel" aria-label="Calls worth calling back">
    <h2>Calls worth calling back</h2>
    {error && <div><p role="alert" className="inline-notice danger">{error}</p><button className="button secondary" disabled={!!busy} onClick={() => { setError(''); setRetry(value => value + 1); }}>Retry loading alerts</button></div>}
    {!!alerts.length && <DataTable rows={alerts.map(row => ({ ...row, caller: row.caller_name || 'Unknown caller', source: SOURCE[String(row.source).toUpperCase()] || row.source, received: formatDateTime(row.occurred_at) }))}
      columns={['caller', 'source', 'received']}
      action={row => <button className="button secondary" disabled={!!busy} onClick={() => void acknowledge(String(row.id))}>{busy === String(row.id) ? 'Saving…' : 'Mark as seen'}</button>} />}
  </section>;
}
